import { useRef } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/ui/Button";
import { DictionaryMeaning } from "services/dictionary";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import "./DictionaryItem.css";

type DictionaryItemProps = {
  pos: string;
  items: DictionaryMeaning[];
};

export default function DictionaryItem({ pos, items }: DictionaryItemProps) {
  return (
    <div className="dictionary-item">
      <div className="dictionary-item-header">
        <span>{pos}</span>
      </div>
      {items.map((item, index) => (
        <DefinitionItem key={index} item={item} />
      ))}
      <div className="separator"></div>
    </div>
  );
}

function DefinitionItem({ item }: { item: DictionaryMeaning }) {
  return (
    <div className="definition-item">
      <div>
        <PronunciationAudio pronunciation={item.pronunciation.audio} />
        {item.pronunciation.phonetic && (
          <div className="transcription">
            <span>{`/${item.pronunciation.phonetic}/`}</span>
          </div>
        )}
        <span className="definition">{item.definition}</span>
        {/* For now, we don't show a term's domain. */}
      </div>
      <div className="examples-list">
        {item.examples.map((example, index) => (
          <div className="example" key={index}>
            {example}
          </div>
        ))}
      </div>
    </div>
  );
}

function PronunciationAudio({ pronunciation }: { pronunciation: string | null }) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onAudioPlay = () => {
    audioRef.current?.play();
  };

  if (!pronunciation) {
    return (
      <div className="pronunciation">
        <VolumeUpIcon
          className="muted"
          role="img"
          aria-label={t("Dictionary.panel.noAudio")}
        />
      </div>
    );
  }

  return (
    <div className="pronunciation">
      <span>
        <Button
          variant="plain"
          className="audio-btn"
          onClick={onAudioPlay}
          aria-label={t("Dictionary.panel.playPronunciation")}
        >
          <VolumeUpIcon />
        </Button>
        {/* key + direct src: mutating a <source> child never reloads an existing media element */}
        <audio
          key={pronunciation}
          src={pronunciation}
          controlsList="nodownload"
          ref={audioRef}
        />
      </span>
    </div>
  );
}
